import {
	CapTableColumn,
	CapTableRow,
} from '@dealstack/frontend/app/pages/Project/Cap/CapTablePage/common/capTableTypes.ts';
import { formatAsOfFilterDate } from '@dealstack/frontend/shared/utils/utc.ts';
import { getStaticColumns } from '@dealstack/frontend/app/pages/Project/Cap/CapTablePage/common/getStaticColumns.tsx';
import {
	insertIpGroupReceiversColumnsAfterIpColumn,
	mapIpGroupsReceiversToColumns,
	mapReceiverToColumn,
} from '@dealstack/frontend/app/pages/Project/Cap/CapTablePage/common/mapReceiverToColumn.tsx';
import {
	CAP_TABLE_EXPAND_ROWS_MODE,
	CAP_TABLE_VALUES_HELD_TYPE,
	CAP_TABLE_VIEW_TYPE,
} from '@dealstack/frontend/app/pages/Project/Cap/CapTablePage/common/tableSettings.ts';
import {
	calculatePercentagesForHoldings,
	isSecurityIssuedInUnits,
	IssuersStack,
	sortSecuritiesAlphabetically,
	sortSecuritiesTypes,
	subtractSecurityFromHoldingTotals,
	subtractSecurityFromSummarizedHolders,
} from '@dealstack/frontend/shared/cap.ts';
import {
	CapTableRail,
	IssuerHolding,
	MipGroupHoldingResponse,
	SecurityHolding,
} from '@dealstack/shared/lib/generated';
import {
	ExpandedState,
	getCoreRowModel,
	getExpandedRowModel,
	Row,
	useReactTable,
	VisibilityState,
} from '@tanstack/react-table';
import { cloneDeep, Dictionary } from 'lodash';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths.ts';
import { getCapTableData } from '@dealstack/frontend/app/services/CapTableData.service.ts';
import { CapHeaderContext } from '@dealstack/frontend/app/pages/Project/Cap/common/CapPageHeader/useCapPageHeaderController.tsx';
import { RowIdentificationKey } from '@dealstack/shared/lib/common/business/cap/capTable.types.ts';

export const useCapTableController = ({
	dealId,
	showSummarized,
	valuesHeldType,
	tableView,
	showPercentages,
	showRowsCheckboxes,
	expandRowsMode,
	setExpandRowsMode,
	isMasterView,
	fundId,
	rail,
}: {
	dealId?: string;
	showSummarized: boolean;
	valuesHeldType: CAP_TABLE_VALUES_HELD_TYPE;
	tableView: CAP_TABLE_VIEW_TYPE;
	showPercentages: boolean;
	showRowsCheckboxes: boolean;
	expandRowsMode: CAP_TABLE_EXPAND_ROWS_MODE;
	setExpandRowsMode: (mode: CAP_TABLE_EXPAND_ROWS_MODE) => void;
	isMasterView?: boolean;
	fundId?: string;
	rail?: CapTableRail;
}) => {
	const { filterDate } = useContext(CapHeaderContext);

	const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
	const [expanded, setExpanded] = useState<ExpandedState>({});
	const [rowSelection, setRowSelection] = React.useState({});
	const [issuersStack, setIssuersStack] = useState<IssuersStack>();

	const getCapTableDataRequest = useQuery({
		queryKey: [
			API_PATHS.CAP_DATA_POST,
			{
				body: {
					dealId: dealId ?? '',
					filterDate: formatAsOfFilterDate(filterDate),
					isMasterView,
					fundId,
					rail,
				},
			},
		],
		queryFn: () =>
			getCapTableData(API_PATHS.CAP_DATA_POST, {
				body: {
					filterDate: formatAsOfFilterDate(filterDate),
					isMasterView,
					fundId,
					rail,
				},
			}),
		enabled: !!dealId,
		// CAP-1973: switching the beneficial/legal rail (or the filter date) changes the query
		// key. Without this the table unmounts behind the full-page loader while the new rail
		// loads — and hasNomineeStructure momentarily reads undefined, so the toggle disappears
		// too. Keep the previous rail's data on screen and swap it in place once the new one
		// arrives, so only the true first load shows the loader (see isLoading below).
		placeholderData: keepPreviousData,
	});

	useEffect(() => {
		if (!getCapTableDataRequest.data) {
			return;
		}
		const { receivers } = getCapTableDataRequest.data;

		const newVisibility = { ...columnVisibility };

		//collapse all mip groups by default
		receivers.forEach((receiver) => {
			if ('investorsKeys' in receiver) {
				receiver.investorsKeys?.forEach((key) => {
					newVisibility[key] = false;
				});
			}
		});

		setColumnVisibility(newVisibility);
	}, [getCapTableDataRequest.data]);

	const staticColumns: CapTableColumn[] = useMemo(() => {
		return getStaticColumns({
			showRowsCheckboxes,
			showSummarized,
			valuesHeldType,
			showPercentages,
			expandRowsMode,
			setExpandRowsMode,
		});
	}, [showRowsCheckboxes, showSummarized, valuesHeldType, showPercentages, expandRowsMode]);

	const receiversColumns = useMemo((): CapTableColumn[] => {
		if (!getCapTableDataRequest.data?.receivers) {
			return [];
		}
		return getCapTableDataRequest.data.receivers.map((receiver) =>
			mapReceiverToColumn({
				receiver: {
					...receiver,
					isIPColumn: !!receiver.isIPColumn,
					investorsKeys: receiver.investorsKeys || [],
					ipId: receiver.ipId || '',
				},
				valuesHeldType,
				showSummarized,
				showPercentages,
			})
		);
	}, [getCapTableDataRequest.data?.receivers, valuesHeldType, showSummarized, showPercentages]);

	const ipGroupReceiversColumns = useMemo((): Dictionary<CapTableColumn[]> | undefined => {
		const ipGroupsReceiversDictionary =
			getCapTableDataRequest.data?.ipGroupsReceiversDictionary;
		if (!ipGroupsReceiversDictionary) {
			return;
		}
		return mapIpGroupsReceiversToColumns({
			ipGroupsReceiversDictionary,
			valuesHeldType,
			showSummarized,
			showPercentages,
		});
	}, [
		getCapTableDataRequest.data?.ipGroupsReceiversDictionary,
		valuesHeldType,
		showSummarized,
		showPercentages,
	]);

	const tableData = useMemo(() => {
		const rows = issuersStack;
		if (!rows) {
			return { tableRows: [], tableColumns: [] };
		}

		const { tableRows, tableColumns } = (() => {
			const defaultOrderColumns = [...staticColumns, ...receiversColumns];

			switch (tableView) {
				case CAP_TABLE_VIEW_TYPE.VIEW_1:
					return { tableRows: rows, tableColumns: defaultOrderColumns };
				case CAP_TABLE_VIEW_TYPE.VIEW_2:
					return {
						tableRows: rows,
						tableColumns: [...staticColumns, ...receiversColumns.slice().reverse()],
					};
				case CAP_TABLE_VIEW_TYPE.VIEW_3:
					return {
						tableRows: rows.slice().reverse(),
						tableColumns: [...staticColumns, ...receiversColumns.slice().reverse()],
					};
				case CAP_TABLE_VIEW_TYPE.VIEW_4:
					return {
						tableRows: rows.slice().reverse(),
						tableColumns: defaultOrderColumns,
					};
				default:
					return { tableRows: rows, tableColumns: defaultOrderColumns };
			}
		})();
		if (!ipGroupReceiversColumns) {
			return { tableRows, tableColumns };
		}

		const ipsIds = Object.keys(ipGroupReceiversColumns);
		const columnsWithIpEntities = insertIpGroupReceiversColumnsAfterIpColumn(
			tableColumns,
			ipGroupReceiversColumns,
			ipsIds
		);

		return { tableRows, tableColumns: columnsWithIpEntities };
	}, [issuersStack, staticColumns, receiversColumns, ipGroupReceiversColumns, tableView]);

	const table = useReactTable<CapTableRow>({
		data: tableData.tableRows,
		columns: tableData.tableColumns,
		state: { expanded, columnVisibility, rowSelection },
		getExpandedRowModel: getExpandedRowModel(),
		getCoreRowModel: getCoreRowModel(),
		onExpandedChange: setExpanded,
		onColumnVisibilityChange: setColumnVisibility,
		onRowSelectionChange: setRowSelection,
		enableRowSelection: ({ original }) => {
			return (
				(RowIdentificationKey.securityTypeSummarizedHolders in original ||
					RowIdentificationKey.securityHolders in original) &&
				isSecurityIssuedInUnits(original.securityIssuedIn)
			);
		},
		getSubRows: (originalRow) => {
			if (RowIdentificationKey.issuersHoldings in originalRow) {
				return originalRow.issuersHoldings;
			}
			if (RowIdentificationKey.issuerSummarizedHolders in originalRow) {
				return sortSecuritiesTypes(originalRow.securityTypesHoldings);
			}
			if (RowIdentificationKey.securityTypeSummarizedHolders in originalRow) {
				return sortSecuritiesAlphabetically(originalRow.securitiesHoldings);
			}
		},
	});

	useEffect(() => {
		if (!getCapTableDataRequest.data) {
			return;
		}

		const selectedRowsIds = new Set(table.getSelectedRowModel().flatRows.map((row) => row.id));
		const allRows = table.getRowModel().flatRows;

		const excludedSecuritiesRows = allRows.filter(
			(row) =>
				RowIdentificationKey.securityHolders in row.original &&
				isSecurityIssuedInUnits(row.original.securityIssuedIn) &&
				!selectedRowsIds.has(row.id)
		) as Row<SecurityHolding>[];

		const issuersStackCopy = cloneDeep(getCapTableDataRequest.data.issuers);

		const issuerMap = new Map<string, IssuerHolding>();
		const mipGroupMap = new Map<string, MipGroupHoldingResponse>();

		issuersStackCopy.forEach((issuer) => {
			if ('issuerId' in issuer) {
				issuerMap.set(issuer.issuerId, issuer);
			} else if ('mipId' in issuer) {
				mipGroupMap.set(issuer.mipId, issuer);
			}
		});

		excludedSecuritiesRows.forEach((excludedSecurityRow) => {
			const securityHolding = excludedSecurityRow.original;

			let issuerHolding;

			if (securityHolding.issuerMipGroupId) {
				const mipGroupHolding = mipGroupMap.get(securityHolding.issuerMipGroupId);

				issuerHolding = mipGroupHolding?.issuersHoldings?.find(
					(issuerHolding) => issuerHolding.issuerId === securityHolding.issuerId
				);
			} else {
				issuerHolding = issuerMap.get(securityHolding.issuerId);
			}

			if (!issuerHolding) {
				return;
			}

			const securityTypeHolding =
				issuerHolding.securityTypesHoldings[securityHolding.securityType];
			if (!securityTypeHolding) {
				return;
			}

			subtractSecurityFromHoldingTotals(issuerHolding.totals, securityHolding.totals);
			subtractSecurityFromSummarizedHolders(
				issuerHolding.issuerSummarizedHolders,
				securityHolding.securityHolders
			);

			subtractSecurityFromHoldingTotals(securityTypeHolding.totals, securityHolding.totals);
			subtractSecurityFromSummarizedHolders(
				securityTypeHolding.securityTypeSummarizedHolders,
				securityHolding.securityHolders
			);
		});

		calculatePercentagesForHoldings(issuersStackCopy);
		setIssuersStack(issuersStackCopy);
	}, [rowSelection, getCapTableDataRequest.data]);

	useEffect(() => {
		if (!expandRowsMode || expandRowsMode === CAP_TABLE_EXPAND_ROWS_MODE.EXPAND_ALL) {
			table.toggleAllRowsExpanded(true);
			return;
		}

		if (expandRowsMode === CAP_TABLE_EXPAND_ROWS_MODE.COLLAPSE_ALL) {
			table.toggleAllRowsExpanded(false);
			return;
		}

		const entitiesRows = table.getPreExpandedRowModel().rows;
		const expandedState: Record<string, boolean> = entitiesRows.reduce((acc, row) => {
			return { ...acc, [row.id]: true };
		}, {});
		table.setExpanded(expandedState);
	}, [expandRowsMode, tableData.tableRows]);

	useEffect(() => {
		table.toggleAllRowsSelected(true);
		if (showRowsCheckboxes) {
			setExpandRowsMode(CAP_TABLE_EXPAND_ROWS_MODE.EXPAND_ALL);
		}
	}, [table.getRowModel().flatRows.length, showRowsCheckboxes]);

	return {
		table,
		// isLoading (not isFetching): true only on the first load when there is no data yet.
		// With placeholderData above, a rail/date refetch keeps isPending false, so the table
		// stays mounted with the previous data instead of flashing the loader.
		isLoading: getCapTableDataRequest.isLoading,
		capTableData: getCapTableDataRequest.data,
	};
};
